/** Commercial plan, independent of the operational `status`. */
export type TenantSubscriptionStatus = "free" | "trial" | "active";

/** Days of the week, Monday first — the order they are displayed in. */
export const TENANT_WEEKDAYS = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
] as const;

export type TenantWeekday = (typeof TENANT_WEEKDAYS)[number];

/** A single opening window within a day, as "HH:mm" 24h wall-clock strings. */
export interface ITenantTimeRange {
    start: string;
    end: string;
}

/**
 * Weekly opening schedule. Times are wall-clock in `timezone`, so the verdict
 * is the same for a customer sitting in another country.
 * An empty range array for a day means "closed all day".
 */
export interface ITenantOpeningHours {
    enabled: boolean;
    timezone: string;
    week: Record<TenantWeekday, ITenantTimeRange[]>;
}

/** A holiday or exceptional closure, inclusive on both ends. */
export interface ITenantClosingDate {
    id: string;
    startDate: string;
    endDate: string;
    label?: string;
}

/** Payload of `GET /tenants/business-hours`. */
export interface ITenantBusinessHours {
    openingHours: ITenantOpeningHours | null;
    closingDates: ITenantClosingDate[] | null;
}

export interface ITenant {
    id: number;
    name: string;
    slug: string;
    status: "active" | "suspended" | "trial";
    subscriptionStatus?: TenantSubscriptionStatus;
    /** Set when the activation link was opened. */
    activatedAt?: string | null;
    /** End of the trial. Null means the tenant never expires. */
    trialEndsAt?: string | null;
    settings?: Record<string, any>;
    pricePerCredit?: number;
    announcementMessage?: string | null;
    autodDecryptFiles?: boolean;
    autoAssembleFiles?: boolean;
    bFlashApiKey?: string | object;
    autotunerMastId?: string | object;
    autotunerApiKey?: string | object;
    alientechLogin?: string | object;
    alientechPassword?: string | object;
    localApiEndpoint?: string;
    logoDark?: string;
    logoLight?: string;
    openingHours?: ITenantOpeningHours | null;
    closingDates?: ITenantClosingDate[] | null;
    createdAt: string;
    updatedAt: string;
}

export interface ICreateTenant {
    name: string;
    slug: string;
    status?: "active" | "suspended" | "trial";
    subscriptionStatus?: TenantSubscriptionStatus;
    trialEndsAt?: string | null;
    settings?: Record<string, any>;
    pricePerCredit?: number;
    announcementMessage?: string | null;
    autodDecryptFiles?: boolean;
    autoAssembleFiles?: boolean;
    bFlashApiKey?: string;
    autotunerMastId?: string;
    autotunerApiKey?: string;
    alientechLogin?: string;
    alientechPassword?: string;
    localApiEndpoint?: string;
    logoDark?: string;
    logoLight?: string;
    openingHours?: ITenantOpeningHours | null;
    closingDates?: ITenantClosingDate[] | null;
}

export interface IUpdateTenant {
    name?: string;
    slug?: string;
    status?: "active" | "suspended" | "trial";
    subscriptionStatus?: TenantSubscriptionStatus;
    trialEndsAt?: string | null;
    settings?: Record<string, any>;
    pricePerCredit?: number;
    announcementMessage?: string | null;
    autodDecryptFiles?: boolean;
    autoAssembleFiles?: boolean;
    bFlashApiKey?: string;
    autotunerMastId?: string;
    autotunerApiKey?: string;
    alientechLogin?: string;
    alientechPassword?: string;
    localApiEndpoint?: string;
    logoDark?: string;
    logoLight?: string;
    openingHours?: ITenantOpeningHours | null;
    closingDates?: ITenantClosingDate[] | null;
}
